import { Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { Action } from '@libraries/vuex-module-decorators/Action';
import Sentry from '@madebyheyday/sentry-util';
import SystemOfUnits from '@app/types/base/SystemOfUnits';
import store from '../store';
import AssetCacheService from '../../services/cache/AssetCacheService';
import StateCacheService from '../../services/cache/StateCacheService';
import AppModule from '../../types/base/AppModule';

@Module({
	name: 'app',
	store,
	dynamic: true
})
export default class AppStoreModule extends VuexModule {
	public isOnline: boolean = true;

	public supportsCaching: boolean = false;

	public isApplicationReadyToBeUpdated: boolean = false;

	public errorsCaptured: Error[] = [];

	// eslint-disable-next-line vuex-module-decorators/no-invalid-store-members
	public activeAppModule: AppModule = AppModule.Unknown;

	// eslint-disable-next-line vuex-module-decorators/no-invalid-store-members
	public localeCode: string = process.env.PRESENTATION_DEFAULT_LOCALE as string;

	// eslint-disable-next-line vuex-module-decorators/no-invalid-store-members
	public systemOfUnits: SystemOfUnits = SystemOfUnits.Metrical;

	@Mutation
	public setIsOnline(isOnline: boolean): void {
		this.isOnline = isOnline;
		Sentry.setTag('appStore.isOnline', isOnline ? 'true' : 'false');
	}

	@Mutation
	public setSupportsCaching(supportsCaching: boolean): void {
		this.supportsCaching = supportsCaching;
	}

	@Mutation
	public setActiveAppModule(appModule: AppModule): void {
		this.activeAppModule = appModule;
	}

	@Mutation
	public setLocale(localeCode: string): void {
		this.localeCode = localeCode;
	}

	@Mutation
	public setSystemOfUnits(systemOfUnits: SystemOfUnits): void {
		this.systemOfUnits = systemOfUnits;
	}

	@Mutation
	public setIsApplicationReadyToBeUpdated(isApplicationReadyToBeUpdated: boolean): void {
		this.isApplicationReadyToBeUpdated = isApplicationReadyToBeUpdated;
	}

	@Action
	public captureError(error: Error | unknown): void {
		if (error instanceof Error) {
			this.errorsCaptured.push(error);
			console.error(error);
			this.processCapturedErrors();
		} else {
			console.error(error);
		}
	}

	@Action
	public processCapturedErrors(): void {
		if (this.isOnline) {
			while (this.errorsCaptured.length > 0) {
				Sentry.captureException(this.errorsCaptured.shift() as Error);
			}
		}
	}

	@Action
	public async resetCaches(): Promise<void> {
		const assetCacheService: AssetCacheService = AssetCacheService.getInstance();

		await assetCacheService.flush();

		await new StateCacheService('auth').clear();
		await new StateCacheService('globals').clear();
		await new StateCacheService('presentations').clear();
		await new StateCacheService('presentationMasters').clear();
		await new StateCacheService('user').clear();
		// Clear legacy table
		await new StateCacheService().clear();
	}
}
